import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiConfig } from '../config/api';
import { getOrderedOffices } from '../config/contact';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const offices = getOrderedOffices();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const footerLinks = {
    'What We Do': [
      { name: 'Orphanage Outreach', path: '/what-we-do#orphanage' },
      { name: 'Education Support', path: '/what-we-do#education' },
      { name: 'Food Bank Project', path: '/what-we-do#foodbank' },
      { name: 'Widow Support', path: '/what-we-do#widows' },
    ],
    'Get Involved': [
      { name: 'Volunteer', path: '/volunteer' },
      { name: 'Donate', path: '/donate' },
      { name: 'Widow Support Application', path: '/widow-application' },
      { name: 'Events', path: '/events' },
      { name: 'Gallery', path: '/gallery' },
    ],
    'Support': [
      { name: 'Contact Us', path: '/contact' },
      { name: 'Staff Clock-In', path: '/clock-in' },
      { name: 'FAQ', path: '/contact#faq' },
      { name: 'Privacy Policy', path: '/privacy' },
      { name: 'Terms of Service', path: '/terms' },
    ],
  };

  const socialLinks = [
    { name: 'Facebook', icon: 'fab fa-facebook-f', url: 'https://facebook.com/abrahamoladotunfoundation' },
    { name: 'Twitter', icon: 'fab fa-twitter', url: 'https://twitter.com/aof_nigeria' },
    { name: 'YouTube', icon: 'fab fa-youtube', url: 'https://youtube.com/abrahamoladotunfoundation' },
  ];

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const API_BASE_URL = apiConfig.baseURL;
      const trimmedEmail = email.trim();
      
      console.log('Sending newsletter subscription request:', {
        url: `${API_BASE_URL}/api/newsletter/subscribe`,
        email: trimmedEmail
      });
      
      const response = await fetch(`${API_BASE_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: trimmedEmail }),
      });

      const result = await response.json();
      console.log('Newsletter subscription response:', result);

      if (result.success) {
        setSubmitStatus('success');
        setEmail('');
        setErrorMessage('');
      } else {
        console.error('Newsletter subscription failed:', result);
        setErrorMessage(result.message || 'Failed to subscribe. Please try again.');
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setErrorMessage('Network error. Please check your connection and try again.');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Foundation Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-16 h-14 rounded-lg overflow-hidden flex-shrink-0">
                <img 
                  src="/images/logo.png" 
                  alt="Abraham Oladotun Foundation Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h3 className="text-lg font-bold">Abraham Oladotun Foundation</h3>
                <p className="text-sm text-gray-400">Helping Children Build Better Futures</p>
              </div>
            </div>
            <p className="text-gray-300 mb-6">
              We are a children's charity organization with a mission to help children better their future. 
              We ensure that no child is left out, focusing on children from 0-18 years.
            </p>
            <div className="space-y-4">
              {offices.map((office) => (
                <div key={office.id}>
                  <p className="text-xs font-semibold tracking-wider text-primary-400 uppercase mb-1">
                    {office.label}
                  </p>
                  <p className="text-sm text-gray-300">
                    <i className="fas fa-building mr-2 text-primary-500"></i>
                    {office.addressLines.join(', ')}
                  </p>
                  {office.phone && (
                    <p className="text-sm text-gray-300">
                      <i className="fas fa-phone mr-2 text-primary-500"></i>
                      <a href={`tel:${office.phoneHref}`} className="hover:text-primary-400 transition-colors">
                        {office.phone}
                      </a>
                    </p>
                  )}
                  <p className="text-sm text-gray-300">
                    <i className="fas fa-envelope mr-2 text-primary-500"></i>
                    <a href={`mailto:${office.email}`} className="hover:text-primary-400 transition-colors break-all">
                      {office.email}
                    </a>
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Footer Links */}
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              <h4 className="text-lg font-semibold mb-4">{title}</h4>
              <ul className="space-y-2">
                {links.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter Signup */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="max-w-md mx-auto text-center">
            <h4 className="text-lg font-semibold mb-4 text-white">Subscribe to Our Newsletter</h4>
            <p className="text-gray-300 mb-6">
              Stay updated with our latest initiatives and impact stories.
            </p>
            
            {submitStatus === 'success' && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                <i className="fas fa-check-circle mr-2"></i>
                Successfully subscribed to our newsletter!
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <i className="fas fa-exclamation-circle mr-2"></i>
                {errorMessage || 'Failed to subscribe. Please try again.'}
              </div>
            )}

            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3">
              <label htmlFor="newsletter-email" className="sr-only">Email address</label>
              <input
                id="newsletter-email"
                type="email"
                placeholder="Enter your email"
                value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (submitStatus === 'error') {
                        setSubmitStatus(null);
                        setErrorMessage('');
                      }
                    }}
                required
                className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={isSubmitting}
              />
              <button 
                type="submit"
                disabled={isSubmitting || !email.trim()}
                className="px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors duration-200 whitespace-nowrap flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Subscribing...
                  </>
                ) : (
                  'Subscribe'
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Social Links and Copyright */}
        <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center">
          <div className="flex space-x-6 mb-4 md:mb-0">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary-400 transition-colors duration-200 text-xl"
                aria-label={social.name}
              >
                <i className={social.icon}></i>
              </a>
            ))}
          </div>
          <p className="text-gray-400 text-sm">
            © {currentYear} Abraham Oladotun Foundation. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
